import { useEffect } from 'react';
import {
  useParams,
  useNavigate,
  generatePath,
  useMatch,
  useOutletContext,
} from 'react-router-dom';
import { SendMessage } from 'react-use-websocket';
import {
  BlacklistDataContent,
  ButtonPseudo,
  OutgoingSidepanel,
  SidePanel,
  SidePanelType,
  BlacklistUserInfo,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  clearTransactionState,
  setPanelIsOpen,
} from '../../store/slices/problem-transactions/outgoing-transaction';
import useProblemOutgoingTransaction from './hooks/use-outgoing';

const ProblemOutgoingSuspiciousTransactionHOC = ({
  matchPath,
  backPath,
  generalPath,
  amlPath,
  orderPath,
  logPath,
  warningPath,
  chatPath,
  documentPath,
  changeLogPath,
  commentsPath,
}: {
  matchPath: string;
  backPath: string;
  generalPath: string;
  amlPath: string;
  orderPath: string;
  logPath: string;
  warningPath: string;
  chatPath: string;
  documentPath: string;
  changeLogPath: string;
  commentsPath: string;
}): React.JSX.Element => {
  const {
    documents,
    document: transactionDocument,
    transaction,
    panelIsOpen,
    warnings,
    blacklistDataVisible,
    blacklistData,
    blacklistItemVisible,
    blacklistItem,
  } = useAppSelector((state) => state.customersProblemTransactions.outgoing);
  const { currencies: currenciesList, activePermissions } = useAppSelector(
    (state) => state.root,
  );

  const { customerId, transactionId } = useParams();

  const navigate = useNavigate();
  const match = useMatch(matchPath);

  const { sendMessage } = useOutletContext<{
    sendMessage: SendMessage | null;
  }>();

  const dispatch = useAppDispatch();

  const {
    search,
    handleClose,
    transactionIsLoading,
    isLoadingAml,
    isLoadingLog,
    warningsIsLoading,
    isFetching,
    downloadInProgress,
    downloadIsFetching,
    imgLink,
    onDeleteDocument,
    deleteDocumentInProgress,
    deleteDocumentIsSuccess,
    onMoveDocument,
    documentCategoryChangeInProgress,
    onDownloadDocument,
    onSendDocumentComment,
    onEditComment,
    editCommentInProgress,
    editCommentIsSuccess,
    onDeleteComment,
    deleteCommentInProgress,
    commentInProgress,
    documentStatusChangeInProgress,
    onChangeCustomerDocumentsStatus,
    downloadFileIsSuccess,
    getTransactionDocumentyIdAction,
    documentIsLoading,
    documentIsFetching,
    handleCloseBlacklistData,
    getOutgoingTransactionById,
  } = useProblemOutgoingTransaction({
    transactionId,
    activePermissions,
    documents,
    transactionDocument,
    customerId,
    transaction,
    backPath,
  });

  useEffect(() => {
    if (customerId && !transaction && transactionId) {
      getOutgoingTransactionById();
    }
  }, [getOutgoingTransactionById, customerId, transaction, transactionId]);

  useEffect(() => {
    dispatch(setPanelIsOpen(true));

    return () => {
      dispatch(clearTransactionState());
    };
  }, [dispatch]);

  useEffect(() => {
    if (match && customerId && transactionId) {
      navigate(
        generatePath(`${generalPath}${search || ''}`, {
          customerId,
          transactionId,
        }),
      );
    }
  }, [match, customerId, transactionId]);

  return (
    <>
      <OutgoingSidepanel
        panelIsOpen={panelIsOpen}
        handleClose={handleClose}
        title="Outgoing Transaction"
        tabs={{
          generalPath: generatePath(`${generalPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          orderPath: generatePath(`${orderPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          amlPath: generatePath(`${amlPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          logPath: generatePath(`${logPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          changeLogPath: generatePath(`${changeLogPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          warningPath: generatePath(`${warningPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          chatTab: generatePath(`${chatPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          documentsPath: generatePath(`${documentPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          commentsPath: generatePath(`${commentsPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
        }}
        isLoading={
          transactionIsLoading ||
          isLoadingAml ||
          isLoadingLog ||
          warningsIsLoading ||
          isFetching
        }
        warnings={warnings}
        document={transactionDocument}
        documentFileIsLoading={downloadInProgress || downloadIsFetching}
        downloadInProgress={downloadInProgress || downloadIsFetching}
        imgLink={imgLink}
        onDelete={onDeleteDocument}
        deleteInProgress={deleteDocumentInProgress}
        deleteIsSuccess={deleteDocumentIsSuccess}
        onMove={onMoveDocument}
        moveInProgress={documentCategoryChangeInProgress}
        onDownload={onDownloadDocument}
        sendComment={onSendDocumentComment}
        editComment={onEditComment}
        editCommentInProgress={editCommentInProgress}
        editCommentIsSuccess={editCommentIsSuccess}
        deleteComment={onDeleteComment}
        deleteCommentInProgress={deleteCommentInProgress}
        commentInProgress={commentInProgress}
        statusChangeInProgress={documentStatusChangeInProgress}
        changeCustomerDocumentsStatus={onChangeCustomerDocumentsStatus}
        onDownloadSuccess={downloadFileIsSuccess}
        getTransactionDocument={getTransactionDocumentyIdAction}
        documentIsLoading={documentIsLoading || documentIsFetching}
        activePermission={activePermissions}
        transactionInfo={transaction}
        sendMessage={sendMessage}
        currenciesList={currenciesList}
        sidepanelExpandedKey="problem-outgoing"
      />
      <SidePanel
        type={SidePanelType.Transactions}
        closeOnClickOutside
        isOpen={blacklistDataVisible}
        onClose={handleCloseBlacklistData}
        title={blacklistData?.fullName as string}
        parent={
          document.getElementById('blacklist-data-sidepanel') as HTMLElement
        }
        footer={
          <ButtonPseudo text="Close" onClick={handleCloseBlacklistData} bold />
        }
        sidepanelExpandedKey="problem-outgoing-blacklist"
      >
        <BlacklistDataContent data={blacklistData} />
      </SidePanel>
      <SidePanel
        type={SidePanelType.Transactions}
        closeOnClickOutside
        isOpen={blacklistItemVisible}
        onClose={handleCloseBlacklistData}
        title="User Details"
        parent={
          document.getElementById('blacklist-data-sidepanel') as HTMLElement
        }
        footer={
          <ButtonPseudo text="Close" onClick={handleCloseBlacklistData} bold />
        }
        sidepanelExpandedKey="problem-outgoing-blacklist"
        withoutExpand
        expanded
      >
        <BlacklistUserInfo info={blacklistItem} isLoading={false} />
      </SidePanel>
      <div id="blacklist-data-sidepanel" />
    </>
  );
};

export default ProblemOutgoingSuspiciousTransactionHOC;
